import React, { useEffect, useRef } from 'react'
import {
  useForm,
  FormProvider,
  type DefaultValues,
  type FieldValues,
  type SubmitHandler,
} from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import type { z } from 'zod'

export type FormProps<TFieldValues extends FieldValues = FieldValues> = {
  children?: React.ReactNode
  className?: string
  defaultValues?: DefaultValues<TFieldValues>
  onValidSubmit?: SubmitHandler<TFieldValues>
  onValid?: () => void
  onInvalid?: () => void
  /** Invalid submit handler (Formsy `onInvalidSubmit` parity). */
  onInvalidSubmit?: (data: FieldValues) => void
  /** Zod schema; output shape should match `TFieldValues` when using `onValidSubmit`. */
  schema?: z.ZodTypeAny
}

/**
 * Form shell (FormProvider + native `<form>`).
 * Prefer `schema` + `defaultValues` for validation. Cross-field rules and i18n messages
 * belong in the schema. Server-side failures: `setError` from `useFormContext()` or
 * `forceError` on a field during migration only.
 */
function Form<TFieldValues extends FieldValues = FieldValues> ({
  children,
  className,
  defaultValues,
  onValidSubmit,
  onValid,
  onInvalid,
  onInvalidSubmit,
  schema,
}: FormProps<TFieldValues>) {
  const methods = useForm({
    defaultValues,
    mode: 'onChange',
    ...(schema
      ? {
        // @ts-expect-error Zod 4 `ZodTypeAny` vs @hookform/resolvers overload (runtime OK)
        resolver: zodResolver(schema),
      }
      : {}),
  })

  const {
    handleSubmit,
    getValues,
    trigger,
    formState: { isValid },
  } = methods

  const prevValidRef = useRef<boolean | null>(null)

  useEffect(() => {
    trigger().then((valid) => {
      prevValidRef.current = valid
      if (valid) {
        if (onValid) {
          onValid()
        }
      } else if (onInvalid) {
        onInvalid()
      }
    })
  }, [trigger, onValid, onInvalid])

  useEffect(() => {
    if (prevValidRef.current === isValid) {
      return
    }
    prevValidRef.current = isValid
    if (isValid) {
      if (onValid) {
        onValid()
      }
    } else if (onInvalid) {
      onInvalid()
    }
  }, [isValid, onValid, onInvalid])

  const onSubmit = onValidSubmit
    ? handleSubmit(
      (data) => {
        onValidSubmit(data as TFieldValues)
      },
      () => {
        if (onInvalidSubmit) {
          onInvalidSubmit(getValues())
        }
      },
    )
    : (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
    }

  return (
    <FormProvider {...methods}>
      <form className={className} noValidate onSubmit={onSubmit}>
        {children}
      </form>
    </FormProvider>
  )
}

export default Form
